// src/pages/Task/RecentlyDeleted.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Trash2, RotateCcw, AlertTriangle, Clock, Search,
  Calendar, Sparkles, ShieldAlert,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { tasksApi } from "../../api/tasks";
import { useAuth } from "../../contexts/AuthContext";

interface DeletedTask {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority: string;
  due_date?: string | null;
  deleted_at?: string | null;
  deleted_by?: string | null;
  created_by?: string | null;
  primary_assignee_id?: string | null;
  client_id?: string | null;
  progress_percent?: number;
}

const PRIORITY_PILLS: Record<string, string> = {
  URGENT: "gt-pill is-urgent",
  HIGH:   "gt-pill is-high",
  MEDIUM: "gt-pill is-medium",
  LOW:    "gt-pill is-low",
};

const ELEVATED = new Set(["admin", "owner", "superadmin", "founder", "director"]);

export default function RecentlyDeleted() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ELEVATED.has((user?.role || "").toLowerCase());

  const [items, setItems] = useState<DeletedTask[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState<{ id: string; mode: "restore" | "purge" } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksApi.listRecentlyDeleted({ days, page: 1, page_size: 100 });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not load recently deleted tasks");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return items;
    return items.filter(t =>
      t.title.toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const onRestore = async (id: string) => {
    setBusy(true);
    try {
      await tasksApi.restore(id);
      toast.success("Task restored");
      setConfirm(null);
      setItems(prev => prev.filter(t => t.id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not restore task");
    } finally {
      setBusy(false);
    }
  };

  const onPurge = async (id: string) => {
    setBusy(true);
    try {
      await tasksApi.permanentDelete(id);
      toast.success("Task permanently deleted");
      setConfirm(null);
      setItems(prev => prev.filter(t => t.id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not delete task");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-6xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        <button onClick={() => navigate(-1)} className="gt-btn is-ghost is-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Header */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">i. Trash</span>
            </div>
            <h1 className="gt-page-title">
              Recently <em>Deleted</em>
            </h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Tasks soft-deleted in the last <em className="gt-serif-italic">{days}</em> days. Restore or remove for good.
            </p>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="gt-search-wrap" style={{ width: 240 }}>
              <Search />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search trash…"
                className="gt-input is-search"
              />
            </div>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="gt-select"
              style={{ width: 'auto' }}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 6 months</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>

        {/* Counter strip */}
        <div className="gt-card p-4 flex items-center gap-3 flex-wrap">
          <span style={{
            width: 36, height: 36, borderRadius: 12,
            background: 'var(--gt-status-overdue-bg)', color: 'var(--gt-prio-urgent-fg)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Trash2 className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <span className="gt-eyebrow" style={{ marginBottom: 0, fontSize: 10 }}>In trash</span>
            <p className="gt-display gt-numerals" style={{ fontSize: 18, fontWeight: 800, color: 'var(--gt-text-primary)' }}>
              {total} task{total !== 1 ? "s" : ""}
            </p>
          </div>
          {!isAdmin && (
            <span className="gt-pill is-ghost">
              <ShieldAlert className="w-3 h-3" /> Permanent delete is admin-only
            </span>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-20"><div className="gt-loading-ring" /></div>
        ) : filtered.length === 0 ? (
          <div className="gt-card p-12">
            <div className="gt-empty">
              <div className="gt-empty-leaf"><Sparkles className="w-6 h-6" /></div>
              <div className="gt-empty-title">Trash is empty</div>
              <div className="gt-page-sub">
                {search ? <>No deleted tasks match <em className="gt-serif-italic">"{search}"</em>.</> : <>No tasks have been deleted in this <em className="gt-serif-italic">window</em>.</>}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2.5">
            <AnimatePresence>
              {filtered.map((t) => (
                <motion.div
                  key={t.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={`gt-task-card is-prio-${(t.priority || 'medium').toLowerCase()} p-4 pl-5 group`}
                >
                  <div className={`gt-prio-bar is-${(t.priority || 'medium').toLowerCase()}`} />

                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="gt-display truncate" style={{ fontSize: 15, fontWeight: 700, color: 'var(--gt-text-primary)' }}>
                        {t.title}
                      </h3>
                      {t.description && (
                        <p className="line-clamp-1" style={{ fontSize: 12.5, color: 'var(--gt-text-tertiary)', marginTop: 4 }}>
                          {t.description}
                        </p>
                      )}

                      <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                        <span className={PRIORITY_PILLS[t.priority] || 'gt-pill is-medium'}>
                          <span className="gt-dot" />
                          {t.priority}
                        </span>
                        <span className="gt-pill is-pending">
                          {t.status.replace(/_/g, ' ')}
                        </span>
                        {t.due_date && (
                          <span className="gt-pill is-ghost gt-numerals">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(t.due_date), "MMM d, yyyy")}
                          </span>
                        )}
                        {t.deleted_at && (
                          <span className="gt-pill is-overdue gt-numerals">
                            <Clock className="w-3 h-3" />
                            Deleted {format(new Date(t.deleted_at), "MMM d, yyyy · h:mm a")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => setConfirm({ id: t.id, mode: 'restore' })}
                        className="gt-btn is-soft is-sm"
                        disabled={busy}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Restore
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => setConfirm({ id: t.id, mode: 'purge' })}
                          className="gt-btn is-danger is-sm"
                          disabled={busy}
                          title="Permanently delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete forever
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirm && (
        <div className="gt-modal-backdrop flex items-center justify-center p-4" onClick={() => !busy && setConfirm(null)}>
          <div className="gt-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-6" style={{ borderBottom: '1px solid var(--gt-divider)' }}>
              <div className="flex items-center gap-3">
                <span style={{
                  width: 44, height: 44, borderRadius: 14,
                  background: confirm.mode === 'restore' ? 'var(--gt-primary-container)' : 'var(--gt-status-overdue-bg)',
                  color: confirm.mode === 'restore' ? 'var(--gt-primary-deep)' : 'var(--gt-prio-urgent-fg)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {confirm.mode === 'restore' ? <RotateCcw className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                </span>
                <h3 className="gt-display" style={{ fontSize: 18 }}>
                  {confirm.mode === 'restore'
                    ? <>Restore <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>Task</em>?</>
                    : <>Delete <em className="gt-serif-italic" style={{ color: 'var(--gt-prio-urgent-fg)' }}>Forever</em>?</>}
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="gt-page-sub">
                {confirm.mode === 'restore'
                  ? <>The task will be restored to your active list and visible again to everyone with access.</>
                  : <><span style={{ color: 'var(--gt-prio-urgent-fg)' }}>This cannot be undone.</span> The task and all its history will be removed permanently.</>}
              </p>
            </div>
            <div className="p-6 flex justify-end gap-3" style={{ borderTop: '1px solid var(--gt-divider)' }}>
              <button onClick={() => setConfirm(null)} className="gt-btn is-ghost" disabled={busy}>Cancel</button>
              <button
                onClick={() => confirm.mode === 'restore' ? onRestore(confirm.id) : onPurge(confirm.id)}
                className={confirm.mode === 'restore' ? 'gt-btn is-primary' : 'gt-btn is-danger'}
                disabled={busy}
              >
                {busy ? '…' : (confirm.mode === 'restore' ? 'Restore' : 'Delete forever')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
